import { EmojiReaction } from '@/common/types/commonTypes';
import { useMutation } from '@tanstack/react-query';
import {
    EntityType,
    emojiReactionService,
} from '../services/emojiReactionService';
import { queryClient } from '@/queryClient';
import { FEEDBACK_KEYS } from '@/features/performance/feedback/services/useFeedbackQueries';
import { useAuthStore } from '@/stores/authStore';
import { useParams } from 'react-router-dom';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';

// 이모지 리액션 쿼리 키 상수
export const EMOJI_REACTION_KEYS = {
    all: ['emojiReactions'] as const,
    feedback: (feedbackId: string) =>
        [...EMOJI_REACTION_KEYS.all, 'feedback', feedbackId] as const,
    dailyScrum: (dailyScrumId: string) =>
        [...EMOJI_REACTION_KEYS.all, 'dailyScrum', dailyScrumId] as const,
};

/**
 * 이모지 리액션 관련 쿼리와 뮤테이션을 제공하는 훅
 * @param entityType 엔티티 타입 ('feedback' 또는 'daily_scrum_item')
 * @param entityId 엔티티 ID
 */
export function useEmojiReactions(entityType: EntityType, entityId: string) {
    const { workspaceId } = useParams();

    // 이모지 리액션 목록 쿼리
    const { data: emojiReactions = [], isLoading } = useSafeQuery({
        queryKey: [...EMOJI_REACTION_KEYS.all, entityType, entityId],
        queryFn: () =>
            emojiReactionService.getEmojiReactions(entityType, entityId),
        enabled: !!entityId,
    });

    // 이모지 리액션 추가 뮤테이션
    const addEmojiMutation = useMutation({
        mutationFn: ({ emojiCode }: { emojiCode: string }) =>
            emojiReactionService.addEmojiReaction(
                entityType,
                entityId,
                emojiCode
            ),
        onSuccess: async () => {
            if (entityType === 'feedback') {
                const user = useAuthStore.getState().user;

                await queryClient.invalidateQueries({
                    queryKey: FEEDBACK_KEYS.receivedByUser(
                        user.id,
                        workspaceId
                    ),
                });

                await queryClient.invalidateQueries({
                    queryKey: [
                        ...EMOJI_REACTION_KEYS.all,
                        entityType,
                        entityId,
                    ],
                });
            }
        },
    });

    // 이모지 리액션 삭제 뮤테이션
    const deleteEmojiMutation = useMutation({
        mutationFn: ({ emojiCode }: { emojiCode: string }) =>
            emojiReactionService.deleteEmojiReaction(
                entityType,
                entityId,
                emojiCode
            ),
        onSuccess: async () => {
            const { user } = useAuthStore.getState();

            await queryClient.invalidateQueries({
                queryKey: FEEDBACK_KEYS.receivedByUser(user.id, workspaceId),
            });

            await queryClient.invalidateQueries({
                queryKey: [...EMOJI_REACTION_KEYS.all, entityType, entityId],
            });
        },
    });

    // 이모지 리액션 토글 함수
    const toggleEmojiReaction = async (
        emojiCode: string,
        isDeleted: boolean
    ) => {
        if (isDeleted) {
            await deleteEmojiMutation.mutateAsync({ emojiCode });
        } else {
            await addEmojiMutation.mutateAsync({ emojiCode });
        }

        // 뮤테이션 완료 후 최신 데이터 반환
        return await queryClient.fetchQuery({
            queryKey: [...EMOJI_REACTION_KEYS.all, entityType, entityId],
            queryFn: () =>
                emojiReactionService.getEmojiReactions(entityType, entityId),
            staleTime: 0, // 항상 최신 데이터를 가져오도록 설정
        });
    };

    return {
        emojiReactions: emojiReactions as EmojiReaction[],
        isLoading:
            isLoading ||
            addEmojiMutation.isPending ||
            deleteEmojiMutation.isPending,
        toggleEmojiReaction,
    };
}
